import { applyPatches } from 'immer';
import { decrypt } from '../../../core/cryptor/cipher';
import {
  PendingQueueItemKey,
  getEntityDBInfo,
} from '../../../core/database/pendingQueue/pendingQueueStorage';
import { Sheet, SheetGroup } from '../../types';
import { createEntityDBName, getSnapshotDB } from '../helpers';
import { SnapshotStores } from '../types';
import {
  GlobalDatabases,
  getGlobalDatabaseInstance,
} from '../../../core/database/manager/databaseManager';
import { PendingQueueStore } from '../../../core/database/schemas/pendingQueueSchema';
import {
  RawCommandsHistory,
  RawCommandsHistoryWithoutChanges,
} from '../../types/sheet/draft';
import {
  SheetGroupsCommand,
  SheetGroupsCommandType,
} from '../../types/commands/sheetGroupsCommands/sheetGroupCommands';

enum DBEntityType {
  Sheet = 'Sheet',
  Group = 'Group',
}

export async function readIDBSheet(
  masterFeKey: CryptoKey,
  sheetId: string,
  feKey: CryptoKey,
  includeUnsentCommands = true
) {
  const sheet = await readSnapshot<Sheet>(sheetId, feKey, DBEntityType.Sheet);
  let rawCommands: RawCommandsHistoryWithoutChanges[] | undefined;
  if (includeUnsentCommands) {
    rawCommands = await findCommandsInUnsentQueue(
      sheetId,
      masterFeKey,
      DBEntityType.Sheet
    );
  }

  return {
    sheet,
    rawCommands,
  };
}

export async function readIDBGroup(
  masterFeKey: CryptoKey,
  groupId: string,
  feKey: CryptoKey,
  includeUnsentCommands = true
) {
  const group = await readSnapshot<SheetGroup>(
    groupId,
    feKey,
    DBEntityType.Group
  );
  let rawCommands: RawCommandsHistoryWithoutChanges[] | undefined;

  if (includeUnsentCommands) {
    rawCommands = await findCommandsInUnsentQueue(
      groupId,
      masterFeKey,
      DBEntityType.Group
    );
  }

  return {
    group,
    rawCommands,
  };
}

// export function* applyUnsentCommands(
//   commands: RawCommandsHistoryWithoutChanges[]
// ) {
//   for (const sheetsCommands of commands) {
//     if (sheetsCommands.creationCommands)
//       for (const command of sheetsCommands.creationCommands) {
//       }
//   }
// }

async function findCommandsInUnsentQueue(
  entityId: string,
  masterFeKey: CryptoKey,
  entityType: DBEntityType
) {
  const db = await getGlobalDatabaseInstance(
    GlobalDatabases.PendingQueue,
    GlobalDatabases.PendingQueue
  );

  const unsortedCommands: Array<{
    position: number;
    command: RawCommandsHistory;
  }> = [];

  const isMatchingRecord = (key: PendingQueueItemKey) => {
    const sheets = key[2];
    const groups = key[3];
    if (
      entityType === DBEntityType.Sheet &&
      sheets &&
      Array.isArray(sheets) &&
      sheets.includes(entityId)
    )
      return true;

    if (
      entityType === DBEntityType.Group &&
      groups &&
      Array.isArray(groups) &&
      groups.includes(entityId)
    )
      return true;
  };

  await db
    .table(PendingQueueStore.UnsentQueue)
    .toCollection()
    .eachPrimaryKey(async (key) => {
      const recordKey = key as PendingQueueItemKey;
      if (Array.isArray(recordKey)) {
        const keyPosition = recordKey[0];
        if (isMatchingRecord(recordKey)) {
          const encRecord = await db
            .table(PendingQueueStore.UnsentQueue)
            .get(key);
          if (encRecord) {
            const command: RawCommandsHistory = await startDecryptJSONData(
              encRecord,
              masterFeKey
            );
            unsortedCommands.push({
              position: keyPosition,
              command,
            });
          }
        }
      }
    });

  const commands = unsortedCommands
    .sort((a, b) => {
      return a.position - b.position;
    })
    .map((record) => record.command);

  return commands;
}

async function readSnapshot<T extends Sheet | SheetGroup>(
  entityId: string,
  feKey: CryptoKey,
  entityType: DBEntityType
) {
  const activeWAL = await getEntityDBInfo(entityId);
  const dbName = createEntityDBName(entityId, activeWAL);

  const db = await getSnapshotDB(dbName);

  const encBase = await db.table(SnapshotStores.Base).get(SnapshotStores.Base);

  let baseState = await startDecryptJSONData(encBase, feKey);
  // console.log('baseState', baseState);
  // In case of group creation if WAL is in unsent DB then the group db will not exists
  // if (!baseState) throw new Error(`Entity with id ${entityId} lost`);

  if (entityType === DBEntityType.Sheet) {
    // In case of group encPatches does not exist
    await db.table(SnapshotStores.Changes).each(async (encPatch) => {
      const patches = await startDecryptJSONData(encPatch, feKey);
      try {
        if (patches) {
          baseState = applyPatches(baseState, patches);
        }
      } catch {}
    });
  } else if (entityType === DBEntityType.Group) {
    await db.table(SnapshotStores.Commands).each(async (encCommands) => {
      const commands: SheetGroupsCommand[] = await startDecryptJSONData(
        encCommands,
        feKey
      );
      try {
        baseState = applyGroupCommands(
          (baseState as SheetGroup) ?? {},
          commands
        );
      } catch {}
    });
  }
  // const encPatches = await db.table(SnapshotStores.Changes).toArray();

  // for (const encPatch of encPatches) {
  //   const patches = await startDecryptJSONData(encPatch, feKey);
  //   try {
  //     if (patches) {
  //       baseState = applyPatches(baseState, patches);
  //     }
  //   } catch {
  //     continue;
  //   }
  // }

  db.close();

  return baseState;
}

function applyGroupCommands(base: SheetGroup, commands: SheetGroupsCommand[]) {
  for (const command of commands) {
    switch (command.type) {
      case SheetGroupsCommandType.AddSheetToGroup:
        base.sheets.push(command.sheetId);
        break;
      case SheetGroupsCommandType.CreateGroup:
        base = command.group;
        break;
      case SheetGroupsCommandType.RenameGroup:
        base = {
          ...base,
          name: command.name,
        };
        break;
      case SheetGroupsCommandType.RemoveSheetFromGroup:
        base = {
          ...base,
          sheets: base.sheets.filter((id) => id !== command.sheetId),
        };
    }
  }

  return base;
}

async function startDecryptJSONData(encData: string, feKey: CryptoKey) {
  try {
    const dec = await decrypt(encData, feKey);
    return JSON.parse(dec);
  } catch (err) {
    return undefined;
  }
}
